// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { PurchaseOrderFormModal } from '~/components/module/purchaseOrder';
import { AdditionalInformation, BasicInformation } from '~/components/module/vendors';
import { BaseStatus, BaseTable, BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
// Others
import { vendorActions } from '~/thunks/vendors/vendorsSlice';
import { IPurchaseOrder } from '~/utils/interface/purchaseOrder';
import { getDetailVendor } from '~/thunks/vendors/vendorsThunk';
import { IPaginationResponse } from '~/utils/interface/base';
import { PurchaseOrderEnum, TypeVendorDetailEnum } from '~/utils/enum/common';
import { IQueryParamDetailVendor } from '~/utils/interface/vendors';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './PurchaseOrdersVendorTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsPurchaseOrder = (t: TFunction): IHeaderCellType<IPurchaseOrder>[] => {
  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_name')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.vendorName}</Box>
        </Stack>
      ),
    },
    {
      id: 'poNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_number')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.poNumber || EMPTY_VALUE}</Box>,
    },
    {
      id: 'neededByDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_needed_by')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.neededByDate || EMPTY_VALUE}</Box>,
    },
    {
      id: 'orderId',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_order')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.orderId || EMPTY_VALUE}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_status')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    // {
    //   id: 'action',
    //   label: t('common_text_action', { ns: 'common/base' }),
    //   minWidth: 100,
    //   align: 'center',
    //   render: (record: IPurchaseOrder) => {
    //     const actions: IBaseMoreAction[] = [
    //       {
    //         label: t('table_action_view', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionView}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         onClick: () => onView(record.id, record),
    //       },
    //       {
    //         label: t('table_action_edit', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionEdit}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         onClick: () => onEdit(record.id),
    //       },
    //       {
    //         label: t('table_action_delete', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionDelete}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         hasOtherColor: true,
    //         onClick: () => onArchive(record.id),
    //       },
    //     ];
    //
    //     return <BaseMoreAction actions={actions} />;
    //   },
    // },
  ];
};

const PurchaseOrdersVendorTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { t } = useTranslation(['admin/vendors', 'common/base']);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { id: vendorId } = useParams();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.vendor);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshDetail } = useAppSelector((state) => state.vendor);
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [paramDetail] = useState<IQueryParamDetailVendor>({
    vendorId: vendorId || EMPTY_STRING,
    type: TypeVendorDetailEnum.PURCHASE_ORDER,
  });
  const [POList, setPOList] = useState<IPurchaseOrder[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [isOpenPurchaseOrderFormModal, setIsOpenPurchaseOrderFormModal] = useState<boolean>(false);

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!isRefreshDetail) return;

    handleGetDetailVendor(paramDetail);

    dispatch(vendorActions.setIsRefreshDetail(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshDetail]);

  useEffect(() => {
    if (!vendorId) return;

    handleGetDetailVendor(paramDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleGetDetailVendor = (params: IQueryParamDetailVendor) => {
    dispatch(getDetailVendor(params))
      .unwrap()
      .then((res) => {
        const { purchaseOrders, pagination } = res?.data;

        if (!purchaseOrders) return;

        setPOList(purchaseOrders);
        setPagination(pagination);
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleAddPO = () => {
    setIsOpenPurchaseOrderFormModal(!isOpenPurchaseOrderFormModal);
  };

  const handleClosePurchaseOrderFormModal = () => {
    setIsOpenPurchaseOrderFormModal(false);
  };
  //#endregion Handle Function

  return (
    <>
      <Box id='infoVendorTabComponent' className={cx('container')}>
        <Grid2 container spacing={6} className={cx('itemBox')}>
          <BaseToolbar title={t('vendor_detail_po_page_title')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAddPO}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('vendor_detail_po_button_add')}
            </Button>
          </BaseToolbar>

          <Grid2 size={6}>
            <BasicInformation />
          </Grid2>

          <Grid2 size={6}>
            <AdditionalInformation />
          </Grid2>
        </Grid2>

        <Box className={cx('vendorTable')}>
          <BaseTable
            dataSource={POList}
            currentPage={pageSelected}
            totalPage={pagination?.totalPages}
            columns={columnsPurchaseOrder(t)}
            onPageChange={handlePageChange}
          />
        </Box>

        {isOpenPurchaseOrderFormModal && (
          <PurchaseOrderFormModal
            vendorId={vendorId}
            onClose={handleClosePurchaseOrderFormModal}
            type={PurchaseOrderEnum.PURCHASE}
          />
        )}

        {isLoading && <Loading />}

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
      </Box>
    </>
  );
};

export default PurchaseOrdersVendorTab;
